import { FC, useState } from 'react'
import {
  BottomNavigation as MuiBottomNavigation,
  BottomNavigationAction,
  SwipeableDrawer,
  Stack,
  Typography,
  List,
  type BottomNavigationActionProps,
  type Theme,
} from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { DialogTitleClosable } from '@dhl/components'
import { ChevronLeft, MoreHorizontal } from '@dhl/icons'

import { MenuItemWithSubItems } from './types.js'
import { SideMenuNodes } from './SideMenuNodes.js'

const sx = {
  drawer: {
    zIndex: 1000,
    p: 1,
  },
  drawerContent: {
    p: 0,
    pb: '52px',
    height: 'calc(100vh - 51px)',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
  },
  action: {
    minWidth: 60,
    color: (theme: Theme) => theme.palette.text.primary,
    // FIXME: is color not enough? what type of icons do we use here
    fill: (theme: Theme) => theme.palette.text.primary,
    '&.active': {
      color: (theme: Theme) => theme.palette.primary.main,
      // FIXME: is color not enough? what type of icons do we use here
      fill: (theme: Theme) => theme.palette.primary.main,
      '& span': {
        fontWeight: 700,
      },
    },
  },
}

type MenuItems = {
  bottomItems: MenuItemWithSubItems[]
  moreItems: MenuItemWithSubItems[]
}

type Props = {
  inlineSubMenuItems: boolean
  menuItems: MenuItemWithSubItems[]
  useAutomaticMoreInMenu?: boolean
}

const getInlinedMenuItems = (items: MenuItemWithSubItems[]): MenuItemWithSubItems[] =>
  items.flatMap((item) => (item.subMenuItems ? getInlinedMenuItems(item.subMenuItems) : item))

const getInlineConvertedMenuItems = (
  menuItems: MenuItemWithSubItems[],
  currentRootPath: string,
  location: ReturnType<typeof useLocation>,
  navigate: ReturnType<typeof useNavigate>,
  setIsMoreDrawerOpen: (value: boolean) => void
): MenuItems => {
  const parentItem = menuItems.find((item) => item.key === currentRootPath)
  const isInSubMenu = parentItem?.subMenuItems?.some((sub) => location.pathname.startsWith(sub.link || '')) ?? false
  let bottomItems: MenuItemWithSubItems[] = []
  let moreItems: MenuItemWithSubItems[] = []

  if (isInSubMenu && parentItem?.subMenuItems) {
    const visibleSubItems = parentItem.subMenuItems.filter((i) => i.showInMobile === true)
    const maxSubItemsInNav = visibleSubItems.length > 4 ? 3 : 4

    const navItems = visibleSubItems.slice(0, maxSubItemsInNav)
    const restItems = visibleSubItems.slice(maxSubItemsInNav)

    bottomItems = [
      {
        key: 'back',
        label: 'Terug',
        icon: <ChevronLeft />,
        onClick: () => navigate(`/`),
      },
      ...navItems,
    ]

    if (restItems.length > 0 && bottomItems.length < 5) {
      bottomItems.push({
        key: 'submenu-more',
        label: 'Meer',
        icon: <MoreHorizontal />,
        onClick: () => setIsMoreDrawerOpen(true),
      })
      moreItems = restItems
    }
  } else {
    const visibleMainItems = menuItems.filter((i) => i.showInMobile === true)
    const maxMainItemsInNav = visibleMainItems.length > 5 ? 4 : 5

    const navItems = visibleMainItems.slice(0, maxMainItemsInNav)
    const restItems = visibleMainItems.slice(maxMainItemsInNav)

    bottomItems = [...navItems]

    if (restItems.length > 0) {
      bottomItems.push({
        key: 'menu-more',
        label: 'Meer',
        icon: <MoreHorizontal />,
        onClick: () => setIsMoreDrawerOpen(true),
      })
      moreItems = restItems
    }
  }

  return {
    bottomItems,
    moreItems,
  }
}

export const BottomNavigation: FC<Props> = ({ inlineSubMenuItems, menuItems, useAutomaticMoreInMenu }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [currentRootPath] = location.pathname.substring(1).split('/')
  const [isMoreDrawerOpen, setIsMoreDrawerOpen] = useState(false)
  let items: MenuItemWithSubItems[] = []
  let moreMenuItems: MenuItemWithSubItems[] = []

  if (useAutomaticMoreInMenu) {
    const { bottomItems, moreItems } = getInlineConvertedMenuItems(
      menuItems,
      currentRootPath,
      location,
      navigate,
      setIsMoreDrawerOpen
    )
    items = bottomItems
    moreMenuItems = moreItems
  } else {
    items = inlineSubMenuItems ? getInlinedMenuItems(menuItems) : menuItems
  }

  const getClassName = (active?: boolean, link?: string) => {
    if (active) {
      return 'active'
    }
    if (link && window.location.pathname.includes(link)) {
      return 'active'
    }
    return ''
  }

  return (
    <>
      <MuiBottomNavigation
        showLabels
        // @deprecated please do use data-testid
        data-test="bottom-navigation"
        data-testid="bottom-navigation"
        sx={{ left: 0 }}
      >
        {items.map(({ icon, label, onClick, key, active, disabled, id, link = '', app, external, target }) => {
          const internalProps: BottomNavigationActionProps = {
            onClick: onClick ?? (() => navigate(link, { state: { app } })),
          }
          const externalProps = {
            href: link,
            target,
            rel: 'noopener noreferrer',
            onClick,
          } satisfies BottomNavigationActionProps<'a'>

          const linkProps = external ? externalProps : internalProps

          return (
            <BottomNavigationAction
              {...linkProps}
              key={key}
              id={id}
              label={label}
              icon={icon}
              sx={sx.action}
              className={getClassName(active, link)}
              data-test={key}
              data-testid={key}
              disabled={disabled}
            />
          )
        })}
      </MuiBottomNavigation>
      {useAutomaticMoreInMenu && (
        <SwipeableDrawer
          onClose={() => setIsMoreDrawerOpen(false)}
          onOpen={() => {}}
          sx={sx.drawer}
          anchor="bottom"
          open={isMoreDrawerOpen}
        >
          <Stack sx={sx.drawerContent}>
            <DialogTitleClosable onClose={() => setIsMoreDrawerOpen(false)}>
              <Typography variant="h4">{t('navigation.otherMenuItems')}</Typography>
            </DialogTitleClosable>
            <List component="nav" data-testid="mobile-drawer-navigation" sx={sx.list}>
              <SideMenuNodes disableTab isExpanded menuItems={moreMenuItems} />
            </List>
          </Stack>
        </SwipeableDrawer>
      )}
    </>
  )
}
